// Local reimplementation of the former @wemap/routers/helpers/InstructionManagerV1.
// See nav-instruction-manager.ts for why this no longer lives in the SDK package.
import { Coordinates } from '@wemap/geo';
import { ItineraryInfoManager, isStepLevelChange } from '@wemap/routers';
import type { Step } from '@wemap/routers';
import { diffAngle, rad2deg } from './angle-math.js';

export default class InstructionManagerV1 {

    static getTurnInfoFromAngle(_angle: number) {

        let direction, directionExtra;

        const directionAngle = rad2deg(diffAngle(_angle, Math.PI));
        const directionAngleAbs = Math.abs(directionAngle);
        if (directionAngleAbs <= 20) {
            direction = 'straight';
        } else {
            direction = directionAngle > 0 ? 'left' : 'right';
            if (directionAngleAbs < 55) {
                directionExtra = 'slight';
            } else if (directionAngleAbs > 120) {
                directionExtra = 'sharp';
            }
        }

        return { direction, directionExtra };
    }

    // eslint-disable-next-line max-statements, complexity
    static getInstructionFromStep(step: Step) {

        const { direction, directionExtra } = InstructionManagerV1.getTurnInfoFromAngle(step.angle);
        const isTurn = direction !== 'straight';

        if (step.lastStep) {
            if (isTurn && direction === 'left') {
                return 'Your destination is on your left';
            } else if (isTurn && direction === 'right') {
                return 'Your destination is on your right';
            }
        }

        let suffix = '';
        if (step.type === 'gate') {
            suffix = ` on gate ${step.name}`;
        } else if (step.name) {
            suffix = ` on ${step.name}`;
        }

        if (isStepLevelChange(step)) {
            if (step.direction === 'up') {
                if (step.type === 'stairs') {
                    return 'Go up the stairs';
                }
                if (step.type === 'escalator') {
                    return 'Go up the escalator';
                }
                if (step.type === 'elevator') {
                    return 'Go up the elevator';
                }
                if (step.type === 'moving-walkway') {
                    return 'Go up the moving walkway';
                }
                if (step.type === 'incline-plane') {
                    return 'Go up the incline plane';
                }
                return 'Go up' + suffix;
            }
            if (step.direction === 'down') {
                if (step.type === 'stairs') {
                    return 'Go down the stairs';
                }
                if (step.type === 'escalator') {
                    return 'Go down the escalator';
                }
                if (step.type === 'elevator') {
                    return 'Go down the elevator';
                }
                if (step.type === 'moving-walkway') {
                    return 'Go down the moving walkway';
                }
                if (step.type === 'incline-plane') {
                    return 'Go down the incline plane';
                }
                return 'Go down' + suffix;
            }
        }

        if (step.type === 'subway-entrance') {
            return `Take exit ${step.name}`;
        }

        if (isTurn) {
            if (direction === 'left') {
                if (directionExtra === 'slight') {
                    return 'Turn slightly left' + suffix;
                }
                return 'Turn left' + suffix;
            }
            if (direction === 'right') {
                if (directionExtra === 'slight') {
                    return 'Turn slightly right' + suffix;
                }
                return 'Turn right' + suffix;
            }
        }

        return 'Continue straight';
    }

    static getInstructionFromPosition(itineraryInfoManager: ItineraryInfoManager, position: Coordinates) {
        const itineraryInfo = itineraryInfoManager.getInfo(position);
        if (!itineraryInfo) {
            return null;
        }

        const { nextStep } = itineraryInfo;
        if (!nextStep) {
            return null;
        }

        const distNextStep = position.distanceTo(nextStep.coords);
        const nextStep2 = itineraryInfoManager.itinerary?.steps.find(step => step.number > nextStep.number);
        if (distNextStep < 3 && nextStep2) {
            return InstructionManagerV1.getInstructionFromStep(nextStep2);
        }

        return InstructionManagerV1.getInstructionFromStep(nextStep);
    }
}
